import { useEffect, useRef } from "react";
import { io, type Socket } from "socket.io-client";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../contexts/auth/Context";
import { useToast } from "../../contexts/toast/Context";
import { decodeUserInfo } from "../../utils/user";
import {
  NOTIFICATIONS_QUERY_KEY,
  NOTIFICATION_UNREAD_COUNT_QUERY_KEY,
  ACCESS_CONTROL_QUERY_KEYS,
  PLATFORM_NOTIFICATIONS_QUERY_KEY,
  PLATFORM_NOTIFICATION_UNREAD_COUNT_QUERY_KEY,
  DAILY_SALES_QUERY_KEY,
} from "../../query/keys";
import { config } from "../../config/config";
import type { ToastType } from "../../contexts/toast/types";
import { realtimeAccessEventSchema } from "../../utils/zod/accessControl";

type IncomingNotification = {
  publicId?: string;
  type?: string;
  title?: string;
  message?: string;
  triggeredByUserPublicId?: string;
};

const NOTIFICATION_TOAST_TYPE: Record<string, ToastType> = {
  PAYMENT_RECEIVED: "success",
  NEW_CLIENT: "success",
  SUBSCRIPTION_EXPIRING: "warning",
  LOW_STOCK: "warning",
  EXPENSE_REMINDER: "warning",
  ACCESS_EXCEPTION: "info",
  SUBSCRIPTION_FROZEN: "info",
  PAYMENT_FAILED: "error",
  PAYMENT_REVERSED: "error",
  SUBSCRIPTION_EXPIRED: "error",
  GATEWAY_DISPUTE: "error",
};

const PLATFORM_NOTIFICATION_TOAST_TYPE: Record<string, ToastType> = {
  APP_BANNER_PENDING: "info",
  GYM_SUBSCRIPTION_EXPIRING: "warning",
  GYM_SUBSCRIPTION_EXPIRED: "error",
  GYM_CREATED: "success",
};

const toastTextOf = (notification: IncomingNotification): string | null => {
  const title = notification?.title?.trim();
  const body = notification?.message?.trim();
  if (!title && !body) return null;
  return title && body ? `${title}: ${body}` : title || body || "";
};

export const useNotificationSocket = () => {
  const { token } = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!token) return;

    const { role, publicId: currentUserPublicId } = decodeUserInfo(token);
    const isPlatform = role === "SUPERADMIN";

    const socket = io(config.API_URL, {
      path: "/ws",
      auth: { token },
      transports: ["polling", "websocket"],
    });
    socketRef.current = socket;

    // Los listeners se registran por rama: el SUPERADMIN solo escucha la room
    // de plataforma, y manager/staff solo los eventos de su gym.
    if (isPlatform) {
      socket.on(
        "platform-notification:new",
        (notification: IncomingNotification) => {
          queryClient.invalidateQueries({
            queryKey: PLATFORM_NOTIFICATIONS_QUERY_KEY,
          });
          queryClient.invalidateQueries({
            queryKey: PLATFORM_NOTIFICATION_UNREAD_COUNT_QUERY_KEY,
          });

          const text = toastTextOf(notification);
          if (!text) return;

          showToast(
            text,
            (notification?.type &&
              PLATFORM_NOTIFICATION_TOAST_TYPE[notification.type]) ||
              "info",
          );
        },
      );

      return () => {
        socket.disconnect();
        socketRef.current = null;
      };
    }

    socket.on("notification:new", (notification: IncomingNotification) => {
      queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY });
      queryClient.invalidateQueries({
        queryKey: NOTIFICATION_UNREAD_COUNT_QUERY_KEY,
      });

      if (
        notification?.triggeredByUserPublicId &&
        notification.triggeredByUserPublicId === currentUserPublicId
      ) {
        return;
      }

      const text = toastTextOf(notification);
      if (!text) return;

      showToast(
        text,
        (notification?.type && NOTIFICATION_TOAST_TYPE[notification.type]) ||
          "info",
      );
    });

    socket.on("access:new", (event: unknown) => {
      const parsed = realtimeAccessEventSchema.safeParse(event);
      if (!parsed.success) return;

      queryClient.invalidateQueries({
        queryKey: ACCESS_CONTROL_QUERY_KEYS.all,
      });

      if (!parsed.data.isException) {
        showToast(`${parsed.data.clientName} entró`, "success");
      }
    });

    socket.on("daily-sales:changed", () => {
      queryClient.invalidateQueries({ queryKey: DAILY_SALES_QUERY_KEY });
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, queryClient, showToast]);
};
